import { PERMISSION_CLASS, can, type Access } from './permissions'
import type { AdminBackend } from '../types/backend'
import type { AuditTargetKind, Permission, Scope } from '../types/team'

/**
 * Per endpoint: welke bevoegdheid, welk bereik, of er een recente 2FA-controle nodig is en wat er
 * in het logboek komt (docs/RECHTEN-EN-SCHAAL.md §2.1, §10.2, §10.7). De demo-server weigert hiermee
 * per aanroep; de server spiegelt dezelfde tabel per route. Geen endpoint zonder regel: het type
 * dwingt elke methode van `AdminBackend` af.
 *
 * `scope: 'own'` = met eigen bereik mag je aanroepen; de functie controleert daarna per rij
 * (`can(access, p, rij)`, 403 `not_your_*`). `scope: 'all'` = alleen met bereik alles.
 */

export type Logged = {
  /** In gewone taal, voor het logboek: "Bon gecorrigeerd". */
  action: string
  kind: AuditTargetKind
  /** Standaard de klasse van de bevoegdheid (alleen C, D en E komen als klasse in het log). */
  cls?: 'C' | 'D' | 'E' | null
}

export type Requirement =
  /** Elke ingelogde sessie (inloggen, 2FA, de inhoud lezen). */
  | { session: true }
  | { p: Permission; scope: Scope; reauth?: boolean; log?: Logged }

const s = { session: true } as const
const own = (p: Permission, log?: Logged): Requirement => ({ p, scope: 'own', ...(log ? { log } : {}) })
const all = (p: Permission, log?: Logged, reauth = false): Requirement => ({ p, scope: 'all', ...(log ? { log } : {}), ...(reauth ? { reauth } : {}) })
const log = (action: string, kind: AuditTargetKind, cls?: Logged['cls']): Logged => ({ action, kind, ...(cls !== undefined ? { cls } : {}) })

export const ENDPOINT_PERMISSIONS: Record<keyof AdminBackend, Requirement> = {
  // ——— Sessie ———
  fetchAdminSession: s,
  adminLogin: s,
  adminLogout: s,
  twoFactorChallenge: s,
  twoFactorSetup: s,
  twoFactorConfirm: s,
  regenerateRecoveryCodes: s,
  reauth: s,
  // Iedereen leest de inhoud (catalogus, team, openingstijden); de server laat marketing weg voor wie
  // `marketing.social` niet heeft. Opslaan vraagt daarnaast per gewijzigd deel de bevoegdheid
  // daarvan (core/rules/contentAccess.ts): wie teksten mag, kan geen prijzen of instellingen opslaan.
  fetchAdminContent: s,
  saveAdminContent: all('content.edit', log('Beheer opgeslagen', 'beheer')),
  updateStock: all('stock.use', log('Voorraad aangepast', 'voorraad')),

  // ——— Klanten ———
  // De kassa kiest er een klant uit (een collega krijgt van de server een smalle lijst).
  fetchCustomers: own('kassa.sell'),
  exportCustomers: all('customers.export', log('Klantbestand geëxporteerd', 'klant'), true),
  fetchCustomer: all('customers.view', log('Klantdossier ingezien', 'klant', 'C')),
  createCustomer: all('customers.edit', log('Klant aangemaakt', 'klant')),
  mergeCustomers: all('customers.merge', log('Dossiers samengevoegd', 'klant')),
  saveCustomerContact: all('customers.edit', log('Contactgegevens aangepast', 'klant')),
  savePlan: all('customers.health', log('Huidplan aangepast', 'klant')),
  saveCourse: all('customers.edit', log('Kuur aangepast', 'kuur')),
  stopKuur: all('kassa.refund', log('Kuur gestopt en terugbetaald', 'kuur')),
  saveNote: all('customers.edit', log('Interne notitie aangepast', 'klant')),
  saveBookingBlock: all('customers.edit', log('Online boeken aan- of uitgezet', 'klant')),
  addAppointment: all('agenda.plan', log('Afspraak ingepland', 'afspraak')),
  confirmAppointment: all('agenda.requests', log('Aanvraag bevestigd', 'afspraak')),
  proposeAppointment: all('agenda.requests', log('Andere tijd voorgesteld', 'afspraak')),
  completeAppointment: all('agenda.complete', log('Afspraak afgerond', 'afspraak')),
  removeAppointment: all('agenda.plan', log('Afspraak geannuleerd', 'afspraak')),
  customerSubscriptionAction: all('subscriptions.manage', log('Abonnement aangepast', 'klant')),
  markDiaryEntry: all('customers.health', log('Huiddagboek gemarkeerd', 'klant')),
  fetchInbox: all('orders.view'),
  setOrderStatus: all('orders.fulfil', log('Bestelling verder gezet', 'bestelling')),
  confirmGuestBooking: all('agenda.requests', log('Aanvraag bevestigd', 'afspraak')),

  // ——— Agenda ———
  fetchAgenda: own('agenda.view'),
  // Een collega: alleen een vervolgafspraak op haar eigen dag (bookingProblem, 403 `not_your_day`).
  addAgendaAppointment: own('agenda.plan', log('Afspraak ingepland', 'afspraak')),
  addAgendaSeries: all('agenda.plan', log('Reeks ingepland', 'afspraak')),
  moveAgendaAppointment: own('agenda.plan', log('Afspraak verzet', 'afspraak')),
  confirmAgendaAppointment: own('agenda.requests', log('Aanvraag bevestigd', 'afspraak')),
  arriveAgendaAppointment: own('agenda.complete'),
  completeAgendaAppointment: own('agenda.complete', log('Afspraak afgerond', 'afspraak')),
  noShowAgendaAppointment: own('agenda.complete', log('Niet gekomen', 'afspraak')),
  cancelAgendaAppointment: own('agenda.plan', log('Afspraak geannuleerd', 'afspraak')),
  // Zetten: nu alleen de eigenaar. Opheffen: een collega bij haar eigen blokkade.
  addAgendaBlock: all('agenda.blocks', log('Tijd geblokkeerd', 'blokkade')),
  removeAgendaBlock: own('agenda.blocks', log('Blokkade opgeheven', 'blokkade')),
  reportAbsence: all('agenda.roster', log('Afwezig gemeld', 'afspraak')),
  fetchAgendaFeed: own('agenda.view'),

  // ——— Team ———
  fetchStaffLogins: all('team.manage'),
  inviteStaff: all('team.manage', log('Uitgenodigd', 'account')),
  revokeStaffLogin: all('team.manage', log('Login ingetrokken', 'account'), true),
  updateStaffAccess: all('team.manage', log('Rol of einddatum aangepast', 'account'), true),
  stopStaff: all('team.manage', log('Gestopt bij de salon', 'account'), true),
  revokeStaffSessions: all('team.manage', log('Uitgelogd op alle apparaten', 'account'), true),
  fetchAuditLog: all('audit.view'),
  // De beperkte klantkaart: alleen als de klant bij haar geboekt staat (403 `not_your_customer`).
  fetchStaffCustomer: own('customers.view', log('Klantkaart ingezien', 'klant', 'C')),
  fetchHandoffNotes: all('customers.edit'),
  addHandoffNote: own('customers.edit', log('Overdrachtsnotitie geschreven', 'klant')),
  markHandoffNoteRead: all('customers.edit'),

  // ——— Bestellingen en omzet ———
  fetchOrders: all('orders.view'),
  fetchOrder: all('orders.view'),
  refundOrder: all('orders.refund', log('Bestelling terugbetaald', 'bestelling')),
  cancelOrder: all('orders.cancel', log('Bestelling geannuleerd', 'bestelling')),
  fetchSalesReport: all('revenue.view'),
  exportSales: all('revenue.export', log('Omzet geëxporteerd', 'beheer')),

  // ——— Inzicht ———
  // Het overzicht zonder `revenue.view`: de server laat de omzet van salon en webshop weg.
  fetchInsightsOverview: all('insights.view'),
  // Klanten (met namen) en de omzet per behandeling of product: niet voor iedereen met Inzicht.
  fetchInsightsCustomers: all('customers.view'),
  fetchInsightsSalon: all('revenue.view'),
  fetchInsightsWebshop: all('revenue.view'),
  fetchInsightsSources: all('insights.view'),
  fetchInsightsVisitors: all('insights.view'),
  fetchInsightsSearch: all('insights.view'),
  fetchInsightsBusiness: all('insights.view'),
  fetchInsightsSocial: all('insights.view'),
  fetchInsightsMail: all('insights.view'),
  fetchIntegrations: all('integrations.manage'),
  refreshIntegration: all('integrations.manage'),

  // ——— Voorraad ———
  // De kassa waarschuwt bij weinig voorraad (een collega krijgt hem zonder inkoopprijzen).
  fetchStock: own('kassa.sell'),
  fetchStockMovements: all('stock.use'),
  addStockMovement: all('stock.use', log('Voorraad geboekt', 'voorraad')),
  addStockItem: all('stock.receive', log('Verbruiksartikel toegevoegd', 'voorraad')),
  patchStockItem: all('stock.cost', log('Inkoopprijs aangepast', 'voorraad')),
  exportStockMovements: all('stock.cost', log('Voorraadlog geëxporteerd', 'voorraad')),
  fetchStockReceipts: all('stock.receive'),
  addStockReceipt: all('stock.receive', log('Inkoop ontvangen', 'voorraad')),
  fetchStockCounts: all('stock.use'),
  addStockCount: all('stock.use', log('Voorraad geteld', 'voorraad')),

  // ——— Kassa ———
  fetchKassaDay: own('kassa.sell'),
  createSale: own('kassa.sell', log('Bon gemaakt', 'bon')),
  voidSale: own('kassa.void', log('Bon gecorrigeerd', 'bon')),
  returnSale: all('kassa.refund', log('Retour op bon', 'bon')),
  closeKassaDay: all('kassa.close', log('Dag afgesloten', 'kassadag')),
  fetchCashDayCloses: all('kassa.totals'),

  // ——— Cadeaubonnen en tegoed ———
  // De kassa checkt een bon op code (een collega: alleen opzoeken, geen lijst).
  fetchVouchers: own('kassa.sell'),
  fetchVoucher: all('vouchers.code', log('Volledige code bekeken', 'cadeaubon')),
  createVoucher: all('vouchers.issue', log('Cadeaubon gemaakt', 'cadeaubon')),
  correctVoucher: all('vouchers.correct', log('Saldo gecorrigeerd', 'cadeaubon')),
  blockVoucher: all('vouchers.correct', log('Geblokkeerd of vrijgegeven', 'cadeaubon')),
  fetchLiabilities: all('liabilities.view'),
  // De kassa laat het tegoed van de klant zien.
  fetchCustomerCredit: own('kassa.sell'),
  correctCustomerCredit: all('customers.credit', log('Tegoed gecorrigeerd', 'tegoed')),

  // ——— Vindbaarheid, mail en nieuwsbrief ———
  fetchRedirects: all('seo.edit'),
  saveRedirects: all('seo.edit', log('Doorverwijzingen opgeslagen', 'instellingen')),
  fetchMailSettings: all('settings.edit'),
  saveMailSettings: all('settings.edit', log('Mailinstellingen opgeslagen', 'instellingen')),
  sendMailTest: all('settings.edit'),
  sendNewsletter: all('marketing.newsletter', log('Nieuwsbrief verstuurd', 'nieuwsbrief')),
  scheduleNewsletter: all('marketing.newsletter', log('Nieuwsbrief ingepland', 'nieuwsbrief')),
  sendNewsletterTest: all('marketing.newsletter'),
  // De lijst met e-mailadressen: klantgegevens, niet voor wie alleen de nieuwsbrief maakt.
  fetchSubscribers: all('customers.view'),
  fetchSegments: all('marketing.newsletter'),
  saveSegments: all('marketing.newsletter', log('Doelgroepen opgeslagen', 'nieuwsbrief')),
}

/** Mag deze sessie dit endpoint aanroepen? (Per rij controleert de functie zelf daarna.) */
export function mayCall(access: Access, key: keyof AdminBackend): boolean {
  const r = ENDPOINT_PERMISSIONS[key]
  if ('session' in r) return true
  return r.scope === 'all' ? access.grants[r.p] === 'all' : can(access, r.p)
}

/** De klasse in het logboek: expliciet, of die van de bevoegdheid als die C, D of E is. */
export function logClass(r: Requirement): 'C' | 'D' | 'E' | null {
  if ('session' in r || !r.log) return null
  if (r.log.cls !== undefined) return r.log.cls
  const c = PERMISSION_CLASS[r.p]
  return c === 'C' || c === 'D' || c === 'E' ? c : null
}
